import PlusIcon from '@heroicons/react/20/solid/PlusIcon'
import CheckCircleIcon from '@heroicons/react/20/solid/CheckCircleIcon'
import clsx from 'clsx'
import { useMemo } from 'react'
import { useMutation } from 'react-relay'
import { toast } from 'react-toastify'

import { useSelectableItemContext } from '../../../components/SelectableItemList/context'
import { getMetaAdListQueryVariables } from '../../../pages/HomeRoute'
import { setCampaignModalOpen } from '../../../state/appSlice'
import { useAppDispatch } from '../../../state/hooks'
import { useTypeParam } from '../../../state/url/type'
import { metaAdApproveMutation } from './mutations/MetaAdApprove'
import { MetaAdApproveMutation } from './mutations/__generated__/MetaAdApproveMutation.graphql'
import { useTrackingContext } from '../../../analytics/useTracking'
import { trackAdApproved } from '../../../analytics/tracking'

interface MetaAdInteractiveOverlayProps {
  metaAd: {
    id: string
    approved: boolean
    created: boolean
    submittedForApproval: boolean
    brand: {
      id: string
    }
  }
}

export const MetaAdInteractiveOverlay = ({
  metaAd: { id, brand, approved, created, submittedForApproval },
}: MetaAdInteractiveOverlayProps) => {
  const { selectedIdLookup, toggleSelect } = useSelectableItemContext()
  const [approveMetaAdRequest, approveMetaAdLoading] =
    useMutation<MetaAdApproveMutation>(metaAdApproveMutation)
  const [type] = useTypeParam()
  const dispatch = useAppDispatch()
  const ctx = useTrackingContext()

  const approveAd = () => {
    if (!approved && !approveMetaAdLoading) {
      const onCompleted = () => {
        toast.success(`Successfully approved ad.`)
        trackAdApproved({ ad_id: id, brand_id: brand.id, account_id: ctx.accountId, is_approved: true }, ctx)
        dispatch(setCampaignModalOpen(false))
      }
      const onError = (error: Error) => {
        console.log(error)
        toast.error(`Unable to approve ad.`)
      }
      approveMetaAdRequest({
        variables: {
          input: {
            id,
            approved: true,
          },
          ...getMetaAdListQueryVariables(type),
        },
        onCompleted,
        onError,
      })
    }
  }

  const handleClick = () => {
    // No interactivity for created ads
    if (created) return

    if (approved) {
      // approved
      toggleSelect(id)
    } else if (submittedForApproval) {
      // unapproved
      approveAd()
    } else {
      // draft
      toggleSelect(id)
    }
  }

  const Icon = useMemo(() => {
    if (approved) {
      // approved
      return PlusIcon
    } else if (submittedForApproval) {
      // unapproved
      return CheckCircleIcon
    } else {
      // draft
      return PlusIcon
    }
  }, [approved, submittedForApproval])

  // No interactive overlay for created ads
  if (created) return null

  return (
    <div
      onClick={handleClick}
      className={clsx(
        'absolute left-0 top-0 z-10 flex h-full w-full cursor-pointer items-center justify-center opacity-0 transition duration-300 hover:opacity-100 ',
        {
          'bg-trueBlue/20 opacity-70': selectedIdLookup[id],
        }
      )}
    >
      <Icon className="h-48 w-48" />
    </div>
  )
}
